import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/Components/ui/tooltip";
import TrafficLightComponent from "./TrafficLightComponent";
import { Card, CardHeader, CardTitle } from "./ui/card";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AppContext } from "@/App";
import { useIsMobile } from "@/hooks/use-mobile";

export type WorkstreamProgress = {
    name: string;
    workstreamId: number;
    actual: number;
    planned: number;
    scheduleStatus: 1 | 2 | 4;
}

export type WorkstreamProgressChartProps = {
    workstreamProgress: WorkstreamProgress[];
    isMultiProject: boolean;
}

function WorkstreamProgressChart({ workstreamProgress, isMultiProject = false }: WorkstreamProgressChartProps) {
    const app = useContext(AppContext);
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    function handleScheduleClick(workstreamId: number, filterCombo: number) {
        if (isMultiProject) {
            // in multi-project mode, workstreamId is the project ID
            app.setSelectedProject(app.projects.find(project => project.id === workstreamId)!);
            navigate('/Plan', { state: { filterCombo: filterCombo } });
        } else {
            navigate('/Plan', { state: { filterCombo: filterCombo, workstreamId: workstreamId } });
        }
    }

    function handleProjectNameClick(workstreamId: number) {
        app.setSelectedProject(app.projects.find(project => project.id === workstreamId)!);
        navigate('/StatusReports');
    }

    if (isMobile) {
        return (
        <Card className="flex grow flex-col justify-center p-4 transition-all duration-300" id="workstream-progress-chart">
            <CardHeader className="items-center pb-0 text-4xl">
                <CardTitle>{isMultiProject ? "Project" : "Workstream"} Progress Chart</CardTitle>
            </CardHeader>
            <div className="flex flex-col grow">
                {workstreamProgress.map((wp, index) => (
                    <div key={index} className="flex flex-col py-2">
                        <div className="flex flex-row items-center">
                            <div className="flex-shrink-0 mr-2">
                                <TrafficLightComponent TrafficLightColour={wp.scheduleStatus} />
                            </div>
                            <div className="flex-grow">
                                <div 
                                    className="text-2xl font-semibold truncate cursor-pointer" 
                                    onClick={() => handleProjectNameClick(wp.workstreamId)}
                                >
                                    {wp.name}
                                </div>
                            </div>
                            <div className="flex flex-row items-center m-2">
                                <div className="text-center text-2xl border-r pr-2">
                                    <div className="text-xl text-muted-foreground">Actual</div>
                                    <div>{wp.actual}%</div>
                                </div>
                                <div className="text-center text-2xl p-2">
                                    <div className="text-xl text-muted-foreground">Planned</div>
                                    <div>{wp.planned}%</div>
                                </div>
                            </div>
                        </div>
                        <div 
                            className="cursor-pointer"
                            onClick={() => handleScheduleClick(wp.workstreamId, wp.scheduleStatus === 4 ? 0 : wp.scheduleStatus * 128)}
                        >
                            <WorkstreamProgressChartRow actual={wp.actual} planned={wp.planned} />
                        </div>
                    </div>
                ))}
            </div>
        </Card>
        );
    }

    return (
        <Card id="workstreamProgressChartCard" className="flex grow flex-col justify-center p-4 min-w-[600px] transition-all duration-300 ">
            <CardHeader className="items-center pb-0">
                <CardTitle className="text-xl">{isMultiProject ? "Project" : "Workstream"} Progress Chart</CardTitle>
            </CardHeader>
            <div className="flex flex-row grow">
                <div className="flex flex-col w-[25%]">
                    <div className="flex h-12 text-center"><span className="flex my-auto">Project</span></div>
                    {workstreamProgress.map((wp, index) => (
                        <div key={index} className="projectProgressName flex h-12 text-center text-sm font-semibold" onClick={() => handleProjectNameClick(wp.workstreamId)} style={{ cursor: 'pointer' }}>
                            <span className="flex my-auto whitespace-nowrap truncate">{wp.name}</span>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col p-2 w-full">
                    <div className="flex flex-row">
                        <div className="w-8 m-2"></div><div className="text-center align-middle w-16 border-r-2 py-2 border-gray-500">A</div><div className="text-center align-middle w-16 py-2">P</div>
                    </div>
                    {workstreamProgress.map((wp, index) => (
                        <div key={index} className="projectProgressRow flex flex-row hover: cursor-pointer" onClick={() => handleScheduleClick(wp.workstreamId, wp.scheduleStatus === 4 ? 0 : wp.scheduleStatus * 128)}>
                            <div className="flex h-min w-min p-2">
                                <TrafficLightComponent TrafficLightColour={wp.scheduleStatus} />
                            </div>
                            <div className="flex flex-row h-min py-2 my-auto">
                                <div className="text-center align-middle w-16"><span className="">{wp.actual}%</span></div><div className="text-center align-middle w-16"><span>{wp.planned}%</span></div>
                            </div>
                            <div className="flex flex-col min-w-[300px] w-full">
                                <WorkstreamProgressChartRow actual={wp.actual} planned={wp.planned} />
                            </div>
                        </ div>
                    ))}
                </div>
            </div>
        </Card>
    );
}

export default WorkstreamProgressChart;

interface ChartProps {
    actual: number;
    planned: number;
    backgroundColor?: string;
    actualColor?: string;
    plannedColor?: string;
}

export function WorkstreamProgressChartRow({ actual, planned, backgroundColor = "bg-slate-400", actualColor = "bg-chart-actual", plannedColor = "bg-chart-planned" }: ChartProps) {
    const diff = planned - actual;
    const actualTip = <div className="flex flex-row text-muted-foreground place-content-between">
        <span className="pr-4">Actual: </span><span className="bold text-foreground">{actual}%</span>
    </div>

    const plannedTip = <div className="flex flex-row text-muted-foreground place-content-between">
        <span className="pr-4">Planned: </span><span className="bold text-foreground">{planned}%</span>
    </div>

    return (
        <TooltipProvider delayDuration={0}>
            <div className={"flex flex-row h-8 m-2 " + backgroundColor}>
                <div className="flex grow h-8 animate-growx">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className={"flex h-8 " + actualColor} style={{ width: `${actual}%` }}></div>
                        </TooltipTrigger>
                        <TooltipContent className="flex flex-col bg-background">
                            {actualTip}
                            {(actual >= planned) && plannedTip}
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className={"flex h-8 " + plannedColor} style={{ width: `${diff}%` }}></div>
                        </TooltipTrigger>
                        <TooltipContent className="flex flex-col bg-background">
                            <div className="flex flex-row text-muted-foreground place-content-between">
                                <span className="pr-4">Planned: </span><span className="bold text-foreground">{planned}%</span>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>

        </TooltipProvider>
    );
}